import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MessageSquare, FileText, Brain, FolderKanban, Sparkles, Shield, Check,
  ArrowRight, Zap, Building2, PenLine, Search,
} from "lucide-react";
import { BrandLogo, BrandMark } from "@/components/BrandLogo";
import { api } from "@/lib/api";

const FEATURES = [
  { icon: MessageSquare, t: "Ask anything", d: "A premium AI chat that thinks, plans, researches and writes with you." },
  { icon: FileText, t: "Work with files", d: "Upload PDFs, docs and spreadsheets — BDVG AI reads and reasons over them." },
  { icon: Brain, t: "Remembers you", d: "Private memory keeps your business, goals and preferences in context." },
  { icon: FolderKanban, t: "Projects", d: "Group chats, files and instructions so context is never lost." },
  { icon: PenLine, t: "Create content", d: "Draft emails, proposals, plans and campaigns in your brand voice." },
  { icon: Building2, t: "Build My Business Workspace", d: "Describe your business and BDVG AI configures itself around it." },
];

const USE_CASES = ["Develop a business plan", "Research a market", "Analyse a spreadsheet", "Write a proposal", "Summarise a contract", "Brainstorm a strategy"];

const FAQ = [
  ["Is my data private?", "Every account is an isolated workspace. Your chats, files and memory are never shared with other customers."],
  ["Do I need a card to start?", "You can start a free trial. Add a plan any time from Billing."],
  ["Can my team join?", "Yes — Team plans let you invite members and share projects."],
  ["Is BDVG AI a real AI?", "Yes. BDVG AI runs on a real, model-agnostic AI backend, branded as BDVG AI."],
];

export default function Landing() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);

  const go = (register) => nav(register ? "/login?mode=register" : "/login");

  return (
    <div className="min-h-screen bg-black text-white bg-noise">
      {/* Nav */}
      <header className="sticky top-0 z-30 glass border-b border-white/8">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <BrandLogo size={30} textClass="text-lg" />
          <div className="flex items-center gap-2">
            <button onClick={() => go(false)} className="rounded-full px-4 py-2 text-sm text-white/70 hover:text-white" data-testid="landing-signin">Sign In</button>
            <button onClick={() => go(true)} className="rounded-full bg-gradient-to-r from-gold-light to-gold px-4 py-2 text-sm font-semibold text-black shadow-gold-sm" data-testid="landing-start-trial">Start Free Trial</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden diamond-radial">
        <div className="mx-auto max-w-4xl px-5 py-24 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }} className="flex justify-center">
            <BrandMark size={96} glow />
          </motion.div>
          <motion.h1 initial={{ opacity: 1, y: 0 }} animate={{ opacity: 1, y: 0 }} className="mt-8 font-heading text-4xl font-bold tracking-tight sm:text-6xl">
            Your intelligent AI partner for<br /><span className="text-gradient-gold">business, work and ideas.</span>
          </motion.h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-white/55">
            Think bigger. Work smarter. Build with BDVG AI — a premium AI that chats, remembers your business, works with your files and helps you get real work done.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button onClick={() => go(true)} className="flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-light to-gold px-7 py-3.5 font-semibold text-black shadow-gold transition-transform hover:scale-[1.02]" data-testid="hero-cta">
              Start Free Trial <ArrowRight className="h-4 w-4" />
            </button>
            <button onClick={() => go(false)} className="rounded-full border border-white/15 px-7 py-3.5 text-white/80 hover:border-gold/30">Sign In</button>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            {USE_CASES.map((u) => (
              <span key={u} className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs text-white/55">“{u}”</span>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <h2 className="text-center font-heading text-3xl font-bold tracking-tight">One AI. Everything you do.</h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-white/50">Ask anything, work with files, remember your business, build projects and create content.</p>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="rounded-2xl border border-white/8 bg-charcoal p-6 transition-colors hover:border-gold/25">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 text-gold"><f.icon className="h-5 w-5" /></div>
              <h3 className="font-heading text-lg font-semibold">{f.t}</h3>
              <p className="mt-1.5 text-sm text-white/50">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* For Business */}
      <section className="border-y border-white/8 bg-gradient-to-b from-gold/[0.04] to-transparent">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-20 lg:grid-cols-2">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-gold/60">For Business</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight">BDVG AI understands your business.</h2>
            <p className="mt-4 text-white/55">Tell BDVG AI “I run a cleaning company with 12 cleaners in North London” and it configures a workspace around your world — your customers, operations, people, documents and goals — so every answer is in context.</p>
            <ul className="mt-6 space-y-2 text-sm text-white/70">
              {["AI Executive Assistant", "AI Business Advisor", "AI Creative Partner", "Private business memory & context"].map((x) => (
                <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> {x}</li>
              ))}
            </ul>
            <button onClick={() => go(true)} className="mt-7 flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-light to-gold px-6 py-3 font-semibold text-black">Build My Workspace <Sparkles className="h-4 w-4" /></button>
          </div>
          <div className="rounded-2xl border border-white/10 bg-charcoal p-6">
            <div className="flex items-center gap-2 text-sm text-white/40"><Search className="h-4 w-4" /> Ask BDVG AI</div>
            <div className="mt-4 space-y-3 text-sm">
              <div className="ml-auto w-fit rounded-2xl rounded-br-md bg-graphite px-4 py-2 text-white/90">Prepare my morning brief.</div>
              <div className="flex gap-2"><BrandMark size={24} className="mt-0.5" /><div className="rounded-2xl bg-white/[0.03] px-4 py-2 text-white/70">Good morning. 2 invoices are overdue (£576), 3 jobs are scheduled today, and 1 new lead needs a reply…</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <h2 className="text-center font-heading text-3xl font-bold tracking-tight">Simple, scalable pricing</h2>
        <p className="mt-3 text-center text-white/50">Start free. Upgrade as you grow.</p>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {plans.slice(0, 6).map((p) => (
            <div key={p.id} className="flex flex-col rounded-2xl border border-white/8 bg-charcoal p-6 hover:border-gold/25">
              <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-gold" /><h3 className="font-heading text-lg font-semibold">{p.name}</h3></div>
              <p className="mt-1 text-sm text-white/45">{p.description}</p>
              <p className="mt-4 font-heading text-3xl font-bold">{p.price_monthly === 0 ? "Custom" : `£${p.price_monthly}`}<span className="text-sm font-normal text-white/40">{p.price_monthly === 0 ? "" : "/mo"}</span></p>
              <button onClick={() => nav("/pricing")} className="mt-5 rounded-xl border border-gold/30 py-2.5 text-sm font-semibold text-gold hover:bg-gold/10">{p.code === "diamond_pro" ? "Upgrade to Pro" : "View plans"}</button>
            </div>
          ))}
        </div>
      </section>

      {/* Security */}
      <section className="border-y border-white/8">
        <div className="mx-auto flex max-w-4xl flex-col items-center px-5 py-16 text-center">
          <Shield className="h-9 w-9 text-gold" />
          <h2 className="mt-4 font-heading text-2xl font-bold">Private by design</h2>
          <p className="mt-3 max-w-2xl text-white/55">Every account is an isolated workspace. Your chats, files and memory belong only to you. Secure authentication, encrypted transport and tenant isolation enforced at the database level.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 py-20">
        <h2 className="text-center font-heading text-3xl font-bold tracking-tight">Questions</h2>
        <div className="mt-8 space-y-3">
          {FAQ.map(([q, a]) => (
            <div key={q} className="rounded-xl border border-white/8 bg-charcoal p-5">
              <p className="font-medium text-white/90">{q}</p>
              <p className="mt-1.5 text-sm text-white/50">{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA + footer */}
      <section className="mx-auto max-w-4xl px-5 pb-10 text-center">
        <div className="rounded-3xl border border-gold/20 bg-gradient-to-br from-gold/[0.08] to-transparent p-12">
          <BrandMark size={56} glow />
          <h2 className="mt-5 font-heading text-3xl font-bold">Build with BDVG AI today.</h2>
          <button onClick={() => go(true)} className="mt-6 rounded-full bg-gradient-to-r from-gold-light to-gold px-8 py-3.5 font-semibold text-black shadow-gold">Start Free Trial</button>
        </div>
      </section>
      <footer className="border-t border-white/8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 py-8 text-sm text-white/40 sm:flex-row">
          <BrandLogo size={24} textClass="text-sm" />
          <div className="flex gap-5">
            <span className="cursor-pointer hover:text-white/70">Privacy</span>
            <span className="cursor-pointer hover:text-white/70">Terms</span>
            <span className="cursor-pointer hover:text-white/70">Security</span>
            <span className="cursor-pointer hover:text-white/70">Contact</span>
          </div>
          <span>© 2026 BDVG AI</span>
        </div>
      </footer>
    </div>
  );
}
